import React, { useEffect, useState } from "react";
import { useStationConnection } from "@/hooks/useStationConnection";
import { useDemoAudio } from "@/hooks/useDemoAudio";
import { useAudioVisualization } from "@/hooks/useAudioVisualization";
import type { Station } from "@/lib/api-client";

interface EmbedBadgeProps {
	station: Station;
	theme?: "light" | "dark";
	autoplay?: boolean;
}

export function EmbedBadge({ station, theme = "dark", autoplay = true }: EmbedBadgeProps) {
	// Client-side detection (prevent SSR hydration errors)
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	// Detect if this is the demo station
	const isDemoStation =
		station.id === "station_demo00000000000000000000" ||
		station.slug === "demo";

	// Use demo audio hook for demo station, real connection for others
	// Only initialize demo audio if it's actually the demo station (to avoid downloading audio file)
	const demoAudio = useDemoAudio({ enabled: isDemoStation });
	const liveStation = useStationConnection({
		station: isDemoStation ? (null as any) : station,
		autoConnect: autoplay,
	});

	// Select the appropriate connection based on station type
	const {
		isConnected,
		isConnecting,
		connect,
		disconnect,
		nowPlaying,
		isAutoplayBlocked,
		unmute,
	} = isDemoStation
		? { ...demoAudio, nowPlaying: null }
		: liveStation;

	// Audio visualization (client-side only, demo station only for now)
	const BAR_COUNT = 28;
	const audioLevels = useAudioVisualization({
		audioElement:
			isClient && isDemoStation ? demoAudio.audioElement : null,
		isPlaying: isConnected && !isAutoplayBlocked,
		barCount: BAR_COUNT,
		visualizeWhenMuted: true,
		isAutoplayBlocked, // Show simulated idle visualization when autoplay is blocked
	});

	const isLive = station.isActive && station.status === "live";
	const stationUrl = `https://macrowave.co/${station.broadcaster.username}/${station.slug}`;

	const displayNowPlaying = nowPlaying
		? {
				track: nowPlaying.name,
				artist: nowPlaying.artist,
			}
		: station.nowPlaying;

	const handlePlayPause = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		// If autoplay is blocked, unmute the audio
		if (isAutoplayBlocked) {
			unmute();
			return;
		}

		// Normal play/pause behavior
		if (isConnected) {
			disconnect();
		} else {
			connect();
		}
	};

	// Colors
	const bgColor = theme === "dark" ? "#0a0a0a" : "#ffffff";
	const surfaceColor = theme === "dark" ? "#1a1a1a" : "#f5f5f5";
	const borderColor = theme === "dark" ? "#333" : "#e0e0e0";
	const textPrimary = theme === "dark" ? "#ffffff" : "#000000";
	const textSecondary = theme === "dark" ? "#888" : "#666";
	const accentColor = "#6adf47";
	const accentDim = "#4fa935";

	return (
		<div
			className="w-full h-full min-h-[80px] flex items-center justify-center p-4"
			style={{ background: "transparent" }}
		>
			{/* Tape Player Badge */}
			<a
				href={stationUrl}
				target="_blank"
				rel="noopener noreferrer"
				className="block w-full max-w-[500px] group"
				style={{ textDecoration: "none" }}
			>
				<div
					className="transition-all"
					style={{
						background: surfaceColor,
						boxShadow:
							"0 6px 20px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)",
						border: `2px solid ${borderColor}`,
						borderRadius: "999px",
						display: "flex",
						alignItems: "center",
						gap: "12px",
						padding: "6px 8px 6px 10px",
					}}
				>
					{/* Left: Audio Visualizer - App Style */}
					{isClient && (
						<div
							style={{
								display: "flex",
								gap: "1.5px",
								alignItems: "center",
								height: "36px",
								padding: "4px 6px 4px 8px",
								background: "#0a0a0a",
								borderRadius: "8px",
								border: "1px solid #222",
								flexShrink: 0,
								boxShadow: "inset 0 2px 4px rgba(0,0,0,0.3)",
							}}
						>
							{audioLevels.map((level, index) => {
								const height = (isConnected || isAutoplayBlocked)
									? Math.max(3, level * 32) // Scale from 3px to 32px based on audio
									: 3;
								// Make bars colorful when blocked OR playing to grab attention
								const barColor = (isConnected || isAutoplayBlocked) ? "#ff6b35" : "#444";
								const barOpacity = 1; // Always full opacity for maximum visibility
								return (
									<div
										key={index}
										style={{
											width: "2px",
											height: `${height}px`,
											background: barColor,
											opacity: barOpacity,
											borderRadius: "1px",
											boxShadow: (isConnected || isAutoplayBlocked)
												? `0 0 4px ${accentColor}40`
												: "none",
										}}
									/>
								);
							})}
						</div>
					)}

					{/* Center: Station Info */}
					<div
						style={{
							flex: 1,
							minWidth: 0,
							overflow: "hidden",
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								marginBottom: "4px",
							}}
						>
							<div
								style={{
									color: textPrimary,
									fontSize: "16px",
									fontWeight: "bold",
									fontFamily: "Array, monospace",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{station.name}
							</div>
							{isLive && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "4px",
										flexShrink: 0,
									}}
								>
									<div
										style={{
											width: "6px",
											height: "6px",
											borderRadius: "50%",
											background: accentColor,
											boxShadow: `0 0 8px ${accentColor}`,
											animation: "pulse 2s ease-in-out infinite",
										}}
									></div>
									<span
										style={{
											color: accentColor,
											fontSize: "11px",
											fontWeight: "bold",
											fontFamily: "Array, monospace",
											textTransform: "uppercase",
										}}
									>
										LIVE
									</span>
								</div>
							)}
						</div>

						{/* Now Playing or Status */}
						<div
							style={{
								color: textSecondary,
								fontSize: "13px",
								fontFamily: "Array, monospace",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{isLive &&
							isConnected &&
							displayNowPlaying?.artist &&
							displayNowPlaying?.track
								? `${displayNowPlaying.artist} - ${displayNowPlaying.track}`
								: `by @${station.broadcaster.username}`}
						</div>
					</div>

					{/* Right: Play/Pause or Subscribe Button */}
					{isLive ? (
						<button
							onClick={handlePlayPause}
							disabled={isConnecting}
							className="transition-all hover:brightness-110 active:scale-95 disabled:opacity-50"
							style={{
								width: "36px",
								height: "36px",
								borderRadius: "50%",
								background: `linear-gradient(180deg, ${accentColor} 0%, #4fa935 50%, #3a8028 100%)`,
								border: `1px solid rgba(0,0,0,0.8)`,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
								boxShadow: isAutoplayBlocked
									? `0 0 0 0 rgba(106, 223, 71, 0.7)`
									: `0 1px 3px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.2)`,
								animation: isAutoplayBlocked ? "buttonPulse 2s ease-in-out infinite" : "none",
							}}
						>
							{isConnecting ? (
								<svg
									width="16"
									height="16"
									fill="none"
									stroke="#000"
									strokeWidth="2"
									viewBox="0 0 24 24"
								>
									<circle cx="12" cy="12" r="10" opacity="0.25" />
									<path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round">
										<animateTransform
											attributeName="transform"
											type="rotate"
											from="0 12 12"
											to="360 12 12"
											dur="1s"
											repeatCount="indefinite"
										/>
									</path>
								</svg>
							) : isConnected && !isAutoplayBlocked ? (
								<svg width="16" height="16" fill="#000" viewBox="0 0 24 24">
									<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
								</svg>
							) : (
								<svg width="16" height="16" fill="#000" viewBox="0 0 24 24">
									<path d="M8 5v14l11-7z" />
								</svg>
							)}
						</button>
					) : (
						<button
							className="transition-all hover:opacity-80"
							style={{
								padding: "12px 24px",
								borderRadius: "20px",
								background: "transparent",
								border: `2px solid ${accentColor}`,
								color: accentColor,
								cursor: "pointer",
								fontSize: "13px",
								fontWeight: "bold",
								fontFamily: "Array, monospace",
								textTransform: "uppercase",
								flexShrink: 0,
							}}
						>
							Subscribe
						</button>
					)}
				</div>

				{/* Macrowave Link (below badge) */}
				<div
					style={{
						marginTop: "8px",
						textAlign: "center",
						fontSize: "11px",
						color: textSecondary,
						fontFamily: "Array, monospace",
					}}
				>
					powered by{" "}
					<span style={{ color: accentColor, fontWeight: "bold" }}>
						macrowave.co
					</span>
				</div>
			</a>

			{/* CSS Animations */}
			<style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes buttonPulse {
          0% {
            box-shadow: 0 0 0 0 rgba(106, 223, 71, 0.7), 0 1px 3px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.2);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(106, 223, 71, 0), 0 1px 3px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.2);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(106, 223, 71, 0), 0 1px 3px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.2);
          }
        }
      `}</style>
		</div>
	);
}
